import React, { ChangeEvent, useState, useEffect, FormEvent } from 'react';
import { useLoaderData } from 'react-router-dom';
import AnimationScreen from '../components/AnimationScreen';
import ProgressBar from '../components/ProgressBar';
import NavigationBar from '../components/NavigationBar';
import { text } from '../data';
import { PageData } from '../types/types';
import PartSearch from '../../components/bearing-search/components/PartSearch';
import { BearingInfo } from '../../components/bearing-search/types/types';
import TextInput from '../../components/bearing-search/components/forms/TextInput';

const BearingSearch: React.FC = () => {
	const { title, progress, prev, content } = useLoaderData() as PageData;
	const { form } = content;
	const [ nextPage, setNextPage ] = useState<string | undefined>( undefined );
	const [ partNumber, setPartNumber ] = useState( '' );
	const [ searchNumber, setSearchNumber ] = useState<string | undefined>( undefined );
	const [ verifiedPart, setVerifiedPart ] = useState<string | undefined>( undefined );
	const [ isPartOpen, setIsPartOpen ] = useState( false );

	useEffect( () => {
		if ( form?.path && verifiedPart ) {
			setNextPage( `/${ form.path }/${ encodeURIComponent( verifiedPart ) }` );
		} else {
			setNextPage( undefined );
		}
	}, [ verifiedPart ] );

	const inputPartNumber = ( event: ChangeEvent<HTMLInputElement> ) => {
		if ( ! form ) {
			return;
		}

		const value = event.target.value;
		setVerifiedPart( undefined );
		setPartNumber( value );
	};

	const handleSubmit = ( event: FormEvent ) => {
		event.preventDefault();
		setSearchNumber( partNumber );
		setIsPartOpen( true );
	};

	const verifyPart = ( part?: BearingInfo ) => {
		if ( part ) {
			const { marketPlaceDescription } = part;
			setVerifiedPart( marketPlaceDescription );
		}

		setPartNumber( part?.marketPlaceDescription || '' );
		setIsPartOpen( false );
	};

	const clearSearch = () => {
		setVerifiedPart( undefined );
		setPartNumber( '' );
	};

	return (
		<AnimationScreen>
			<ProgressBar progress={progress} />
			<h3>{title}</h3>
			<p className="c-guided-help__description">
				{text.contactText}&nbsp;
				<a href="/contact-us" data-link-click>
					{text.contactForm}
				</a>
				{'.'}
			</p>
			{form && (
				<>
					<div className="c-guided-help__form-subtitle">{form.title}</div>
					<form className="c-guided-help__search" onSubmit={handleSubmit}>
						<TextInput
							id={form.id}
							required={true}
							label={form.label}
							verified={!! verifiedPart}
							name="partNumber"
							value={partNumber}
							disabled={!! verifiedPart}
							placeholder={form.placeholder}
							message={verifiedPart ? '' : text.partWildCardHint}
							className="c-guided-help__input"
							onChange={inputPartNumber}
						/>

						{! verifiedPart && (
							<button type="submit" className="btn btn--primary" disabled={! partNumber}>
								{text.lookup}
							</button>
						)}

						{!! verifiedPart && (
							<button type="button" className="btn btn--secondary" onClick={() => clearSearch()}>
								{text.clear}
							</button>
						)}
					</form>

					{isPartOpen && !! searchNumber && (
						<PartSearch partNumber={searchNumber} onSelect={verifyPart} onClose={() => setIsPartOpen( false )} />
					)}
				</>
			)}
			<NavigationBar prev={prev} next={nextPage} />
		</AnimationScreen>
	);
};

export default BearingSearch;
